import { StateCreator } from "zustand";

import type { SearchStore } from "@/stores/search";
import {
  SearchSession,
  SearchSessionGoals,
  SearchSessionStats,
  SessionInterestCoins,
  SessionShortlist,
  StartSearchSessionResponse,
} from "@/types";

// Activity gaps longer than this count as idle; the excess past the threshold
// accumulates into idleMs and is cut from the session's duration server-side.
export const SEARCH_SESSION_IDLE_THRESHOLD_MS = 30_000;
// High-frequency input (mousemove, scroll) only touches the clock this often.
const INPUT_THROTTLE_MS = 1_000;

export const NO_INTEREST_COINS: SessionInterestCoins = {
  coins: 0,
  goalBonusCoins: 0,
};

// idle: nothing running. live: the search tab is mounted. paused: the tab
// unmounted with a session still open; a return within the pause timeout
// resumes it, otherwise the timer ends it for real.
export type SearchSessionStatus = "idle" | "live" | "paused";

export interface SessionSliceState {
  status: SearchSessionStatus;
  jobId: string | null;
  session: SearchSession | null;
  stats: SearchSessionStats | null;
  goals: SearchSessionGoals | null;
  startedAtMs: number;
  interestCount: number;
  // Talent ids already counted toward this session's interest total — the
  // backend only credits the first interest per talent.
  countedInterestTalentIds: Set<number>;
  interestCoins: SessionInterestCoins;
  shortlists: SessionShortlist[];
  // Ends the paused session when it expires; cleared on resume.
  pauseTimeoutId: ReturnType<typeof setTimeout> | null;
}

export interface SessionSliceActions {
  beginSession: (jobId: string, response: StartSearchSessionResponse) => void;
  resumeSession: () => void;
  pauseSession: (pauseTimeoutId: ReturnType<typeof setTimeout>) => void;
  // Clears the session and the iteration inside it. Review results are kept:
  // the review queue is the server's interest rows, not session state.
  resetSession: () => void;
  // Advances the activity clock; returns the gap since the previous touch so
  // the caller can decide the session went stale. Throttled gaps return null.
  touchActivity: (nowMs?: number) => number | null;
  // Folds the trailing gap into idleMs and returns the total for the end call.
  flushIdleMs: (nowMs?: number) => number;
  recordInterest: (talentId: number) => void;
  recordInterestCoins: (awarded: SessionInterestCoins) => void;
  recordShortlist: (shortlist: SessionShortlist) => void;
  // Live snapshot for the progress bar: active time with idle cut, and
  // whether the clock is currently paused by inactivity.
  getSessionProgress: (nowMs?: number) => { activeMs: number; isIdle: boolean };
}

export type SessionSlice = SessionSliceState & SessionSliceActions;

const initialState: SessionSliceState = {
  status: "idle",
  jobId: null,
  session: null,
  stats: null,
  goals: null,
  startedAtMs: 0,
  interestCount: 0,
  countedInterestTalentIds: new Set(),
  interestCoins: NO_INTEREST_COINS,
  shortlists: [],
  pauseTimeoutId: null,
};

const idleBeyondThreshold = (gapMs: number) =>
  Math.max(gapMs - SEARCH_SESSION_IDLE_THRESHOLD_MS, 0);

// The activity clock is deliberately NOT store state. It moves on every
// throttled mouse/scroll/key event; putting it through `set` re-rendered the
// whole search tree once a second and, worse, forced a synchronous React
// commit inside the keydown dispatch — listeners re-attached mid-event were
// skipped, which is how the review modal's R key went dead. Nothing renders
// from it; the progress bar polls `getSessionProgress` on its own timer.
const activityClock = { idleMs: 0, lastActivityAtMs: 0 };

const resetActivityClock = (nowMs: number) => {
  activityClock.idleMs = 0;
  activityClock.lastActivityAtMs = nowMs;
};

export const createSessionSlice: StateCreator<
  SearchStore,
  [],
  [],
  SessionSlice
> = (set, get) => ({
  ...initialState,

  beginSession: (jobId, response) => {
    const nowMs = Date.now();
    resetActivityClock(nowMs);
    set({
      ...initialState,
      countedInterestTalentIds: new Set(),
      status: "live",
      jobId,
      session: response.session,
      stats: response.stats,
      goals: response.goals,
      startedAtMs: nowMs,
    });
  },

  resumeSession: () => {
    const { pauseTimeoutId } = get();
    if (pauseTimeoutId) clearTimeout(pauseTimeoutId);
    set({ status: "live", pauseTimeoutId: null });
  },

  pauseSession: (pauseTimeoutId) => {
    set({ status: "paused", pauseTimeoutId });
  },

  resetSession: () => {
    const { pauseTimeoutId, resetIteration } = get();
    if (pauseTimeoutId) clearTimeout(pauseTimeoutId);
    resetActivityClock(0);
    set({ ...initialState, countedInterestTalentIds: new Set() });
    resetIteration();
  },

  touchActivity: (nowMs = Date.now()) => {
    const gapMs = nowMs - activityClock.lastActivityAtMs;
    if (gapMs < INPUT_THROTTLE_MS) return null;
    activityClock.idleMs += idleBeyondThreshold(gapMs);
    activityClock.lastActivityAtMs = nowMs;
    return gapMs;
  },

  flushIdleMs: (nowMs = Date.now()) => {
    const { idleMs, lastActivityAtMs } = activityClock;
    const trailingGapMs = lastActivityAtMs ? nowMs - lastActivityAtMs : 0;
    return idleMs + idleBeyondThreshold(trailingGapMs);
  },

  // Deduped by talentId: an interest/un-interest/re-interest loop must not
  // tick the visual counters past what the server will confirm.
  recordInterest: (talentId) => {
    const { countedInterestTalentIds, interestCount } = get();
    if (countedInterestTalentIds.has(talentId)) return;
    set({
      countedInterestTalentIds: new Set(countedInterestTalentIds).add(talentId),
      interestCount: interestCount + 1,
    });
  },

  // Coins arrive on the upsert response, after recordInterest's optimistic
  // count — tracked separately.
  recordInterestCoins: (awarded) => {
    if (!awarded.coins && !awarded.goalBonusCoins) return;
    const { interestCoins } = get();
    set({
      interestCoins: {
        coins: interestCoins.coins + awarded.coins,
        goalBonusCoins: interestCoins.goalBonusCoins + awarded.goalBonusCoins,
      },
    });
  },

  recordShortlist: (shortlist) => {
    set({ shortlists: [...get().shortlists, shortlist] });
  },

  getSessionProgress: (nowMs = Date.now()) => {
    const { startedAtMs } = get();
    const { idleMs, lastActivityAtMs } = activityClock;
    if (!startedAtMs) return { activeMs: 0, isIdle: false };
    const gapMs = lastActivityAtMs ? nowMs - lastActivityAtMs : 0;
    const totalIdleMs = idleMs + idleBeyondThreshold(gapMs);
    return {
      activeMs: Math.max(nowMs - startedAtMs - totalIdleMs, 0),
      isIdle: gapMs > SEARCH_SESSION_IDLE_THRESHOLD_MS,
    };
  },
});
